import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import supabase from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiArrowLeft, FiAlertCircle, FiEdit, FiTrash2, FiPlus, FiX, FiCalendar, FiClock, FiFilter, FiExternalLink } = FiIcons;

const EMPTY_MATCH = {
  date: new Date().toISOString().slice(0, 10),
  time: '17:00',
  home_team: 'ΑΟ ΧΑΝΙΑ',
  away_team: '',
  home_score: '',
  away_score: '',
  location: '',
  status: 'upcoming',
  tournament: "Γ' Κατηγορία Γυναικών",
  team_category: 'Senior',
  round_label: '',
  stream_url: '',
  poster_image_url: '',
  home_logo_url: '',
  away_logo_url: '',
  match_report: '',
  scorers: '',
  player_of_match: '',
  is_featured: false,
};

const STATUS_OPTIONS = [
  ['upcoming', 'Επερχόμενος'],
  ['live', 'LIVE'],
  ['completed', 'Ολοκληρώθηκε'],
  ['canceled', 'Ακυρώθηκε'],
  ['postponed', 'Αναβλήθηκε'],
];

const TEAM_CATEGORIES = ['Senior', 'K17', 'K14', 'K12', 'K10', 'K6'];

const Matches = () => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_MATCH);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filterCategory, setFilterCategory] = useState('all');

  const fetchMatches = async () => {
    setLoading(true);
    const { data, error: queryError } = await supabase.from('matches_aox1234').select('*').order('date', { ascending: false }).order('time', { ascending: false });
    if (queryError) setError(queryError.message);
    else {
      setError('');
      setMatches(data || []);
    }
    setLoading(false);
  };

  useEffect(() => { fetchMatches(); }, []);

  const filteredMatches = useMemo(() => filterCategory === 'all' ? matches : matches.filter((match) => (match.team_category || 'Senior') === filterCategory), [matches, filterCategory]);

  const onChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const resetForm = () => {
    setFormData(EMPTY_MATCH);
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (match) => {
    setFormData({
      ...EMPTY_MATCH,
      ...match,
      date: String(match.date || '').slice(0, 10),
      time: match.time ? String(match.time).slice(0, 5) : '17:00',
      home_score: match.home_score ?? '',
      away_score: match.away_score ?? '',
      is_featured: Boolean(match.is_featured),
    });
    setEditingId(match.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage({ type: '', text: '' });

    const payload = {
      date: formData.date,
      time: formData.time || null,
      home_team: formData.home_team.trim(),
      away_team: formData.away_team.trim(),
      home_score: formData.home_score === '' ? null : Number(formData.home_score),
      away_score: formData.away_score === '' ? null : Number(formData.away_score),
      location: formData.location.trim(),
      status: formData.status,
      tournament: formData.tournament.trim(),
      team_category: formData.team_category,
      round_label: formData.round_label?.trim() || null,
      stream_url: formData.stream_url?.trim() || null,
      poster_image_url: formData.poster_image_url?.trim() || null,
      home_logo_url: formData.home_logo_url?.trim() || null,
      away_logo_url: formData.away_logo_url?.trim() || null,
      match_report: formData.match_report?.trim() || null,
      scorers: formData.scorers?.trim() || null,
      player_of_match: formData.player_of_match?.trim() || null,
      is_featured: Boolean(formData.is_featured),
      updated_at: new Date().toISOString(),
    };

    const result = editingId
      ? await supabase.from('matches_aox1234').update(payload).eq('id', editingId)
      : await supabase.from('matches_aox1234').insert([{ ...payload, created_at: new Date().toISOString() }]);

    if (result.error) {
      setMessage({ type: 'error', text: `Σφάλμα: ${result.error.message}` });
      return;
    }

    setMessage({ type: 'success', text: editingId ? 'Ο αγώνας ενημερώθηκε.' : 'Ο αγώνας δημιουργήθηκε.' });
    resetForm();
    fetchMatches();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Να διαγραφεί ο αγώνας;')) return;
    const { error: deleteError } = await supabase.from('matches_aox1234').delete().eq('id', id);
    if (deleteError) setMessage({ type: 'error', text: deleteError.message });
    else {
      setMessage({ type: 'success', text: 'Ο αγώνας διαγράφηκε.' });
      fetchMatches();
    }
  };

  const formatDate = (value) => value ? new Date(`${String(value).slice(0, 10)}T12:00:00`).toLocaleDateString('el-GR') : '-';
  const statusLabel = (value) => STATUS_OPTIONS.find(([key]) => key === value)?.[1] || value;
  const statusStyle = (value) => ({ live: 'bg-red-100 text-red-800', completed: 'bg-green-100 text-green-800', postponed: 'bg-yellow-100 text-yellow-800', canceled: 'bg-gray-200 text-gray-700' }[value] || 'bg-blue-100 text-blue-800');

  const Field = ({ label, name, type = 'text', required = false, placeholder = '' }) => (
    <label className="block">
      <span className="block text-sm font-semibold text-gray-700 mb-1">{label}{required ? ' *' : ''}</span>
      <input type={type} name={name} required={required} value={formData[name] ?? ''} onChange={onChange} placeholder={placeholder} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500" />
    </label>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/dashboard')} className="p-2 rounded-lg hover:bg-gray-100"><SafeIcon icon={FiArrowLeft} className="w-5 h-5" /></button>
            <div><h1 className="text-xl font-black">Αγώνες & Match Center</h1><p className="text-xs text-gray-500">Matchday, LIVE, μετάδοση, report και στοιχεία αγώνα</p></div>
          </div>
          <button onClick={() => showForm ? resetForm() : setShowForm(true)} className="inline-flex items-center rounded-lg bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 font-bold"><SafeIcon icon={showForm ? FiX : FiPlus} className="w-4 h-4 mr-2" />{showForm ? 'Κλείσιμο' : 'Νέος Αγώνας'}</button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {message.text && <div className={`mb-6 rounded-xl p-4 flex items-center ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}><SafeIcon icon={FiAlertCircle} className="w-5 h-5 mr-2" />{message.text}</div>}

        {showForm && (
          <motion.form initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} onSubmit={handleSubmit} className="mb-8 rounded-2xl bg-white shadow-sm border border-gray-200 p-6 space-y-8">
            <div className="flex items-center justify-between"><h2 className="text-2xl font-black">{editingId ? 'Επεξεργασία Αγώνα' : 'Νέος Αγώνας'}</h2>{editingId && <a href={`/matches/${editingId}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-sm font-bold text-primary-600">Preview Match Center <SafeIcon icon={FiExternalLink} className="w-4 h-4 ml-1" /></a>}</div>

            <section>
              <h3 className="text-sm uppercase tracking-[0.16em] font-black text-primary-700 mb-4">Βασικά στοιχεία</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Field label="Ημερομηνία" name="date" type="date" required />
                <Field label="Ώρα" name="time" type="time" required />
                <label className="block"><span className="block text-sm font-semibold text-gray-700 mb-1">Κατηγορία</span><select name="team_category" value={formData.team_category} onChange={onChange} className="w-full rounded-lg border border-gray-300 px-3 py-2">{TEAM_CATEGORIES.map((item) => <option key={item} value={item}>{item === 'Senior' ? "Α' Ομάδα" : item}</option>)}</select></label>
                <label className="block"><span className="block text-sm font-semibold text-gray-700 mb-1">Κατάσταση</span><select name="status" value={formData.status} onChange={onChange} className="w-full rounded-lg border border-gray-300 px-3 py-2">{STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                <Field label="Γηπεδούχος" name="home_team" required />
                <Field label="Φιλοξενούμενος" name="away_team" required />
                <Field label="Γκολ γηπεδούχου" name="home_score" type="number" />
                <Field label="Γκολ φιλοξενούμενου" name="away_score" type="number" />
                <Field label="Διοργάνωση" name="tournament" required />
                <Field label="Αγωνιστική / Γύρος" name="round_label" placeholder="π.χ. 1η Αγωνιστική" />
                <Field label="Γήπεδο" name="location" />
                <label className="flex items-center gap-3 mt-7"><input type="checkbox" name="is_featured" checked={Boolean(formData.is_featured)} onChange={onChange} className="h-5 w-5 rounded border-gray-300" /><span className="font-bold text-gray-700">Featured αγώνας</span></label>
              </div>
            </section>

            <section>
              <h3 className="text-sm uppercase tracking-[0.16em] font-black text-primary-700 mb-4">Matchday & Media</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Link μετάδοσης" name="stream_url" type="url" placeholder="YouTube / Facebook / άλλη μετάδοση" />
                <Field label="Match poster image URL" name="poster_image_url" type="url" />
                <Field label="Logo γηπεδούχου URL" name="home_logo_url" type="url" />
                <Field label="Logo φιλοξενούμενου URL" name="away_logo_url" type="url" />
              </div>
            </section>

            <section>
              <h3 className="text-sm uppercase tracking-[0.16em] font-black text-primary-700 mb-4">Μετά τον αγώνα</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block md:col-span-2"><span className="block text-sm font-semibold text-gray-700 mb-1">Αναφορά αγώνα</span><textarea name="match_report" rows="6" value={formData.match_report || ''} onChange={onChange} className="w-full rounded-lg border border-gray-300 px-3 py-2" /></label>
                <label className="block"><span className="block text-sm font-semibold text-gray-700 mb-1">Σκόρερ</span><textarea name="scorers" rows="3" value={formData.scorers || ''} onChange={onChange} className="w-full rounded-lg border border-gray-300 px-3 py-2" /></label>
                <Field label="Player of the Match" name="player_of_match" />
              </div>
            </section>

            <div className="flex justify-end gap-3 border-t pt-5"><button type="button" onClick={resetForm} className="px-5 py-2 rounded-lg border font-bold">Ακύρωση</button><button type="submit" className="inline-flex items-center px-5 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-black"><SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />Αποθήκευση</button></div>
          </motion.form>
        )}

        <div className="bg-white rounded-xl shadow-sm border p-4 mb-6 flex flex-wrap items-center gap-4"><SafeIcon icon={FiFilter} className="w-5 h-5 text-gray-400" /><select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="border rounded-lg py-2 px-3 text-sm"><option value="all">Όλες οι κατηγορίες</option>{TEAM_CATEGORIES.map((item) => <option key={item} value={item}>{item}</option>)}</select></div>

        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          {loading ? <div className="py-12 text-center">Φόρτωση...</div> : error ? <div className="m-6 bg-red-50 text-red-700 p-4 rounded-lg">{error}</div> : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50"><tr><th className="px-5 py-3 text-left text-xs uppercase text-gray-500">Ημερομηνία</th><th className="px-5 py-3 text-left text-xs uppercase text-gray-500">Κατηγορία</th><th className="px-5 py-3 text-left text-xs uppercase text-gray-500">Αγώνας</th><th className="px-5 py-3 text-left text-xs uppercase text-gray-500">Διοργάνωση</th><th className="px-5 py-3 text-left text-xs uppercase text-gray-500">Κατάσταση</th><th className="px-5 py-3 text-right text-xs uppercase text-gray-500">Ενέργειες</th></tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredMatches.map((match) => (
                    <tr key={match.id} className="hover:bg-gray-50">
                      <td className="px-5 py-4 whitespace-nowrap"><div className="font-bold">{formatDate(match.date)}</div><div className="text-sm text-gray-500"><SafeIcon icon={FiClock} className="inline w-3 h-3 mr-1" />{match.time ? String(match.time).slice(0, 5) : '-'}</div></td>
                      <td className="px-5 py-4"><span className="rounded-full bg-purple-100 text-purple-800 px-2.5 py-1 text-xs font-bold">{match.team_category || 'Senior'}</span></td>
                      <td className="px-5 py-4"><div className="font-bold">{match.home_team} {['completed', 'live'].includes(match.status) ? `${match.home_score ?? 0} - ${match.away_score ?? 0}` : 'vs'} {match.away_team}</div>{match.round_label && <div className="text-xs text-gray-500 mt-1">{match.round_label}</div>}</td>
                      <td className="px-5 py-4 text-sm">{match.tournament || '-'}</td>
                      <td className="px-5 py-4"><span className={`rounded-full px-2.5 py-1 text-xs font-bold ${statusStyle(match.status)}`}>{statusLabel(match.status)}</span></td>
                      <td className="px-5 py-4 text-right whitespace-nowrap"><a href={`/matches/${match.id}`} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-900 mr-4" title="Match Center"><SafeIcon icon={FiExternalLink} className="inline w-4 h-4" /></a><button onClick={() => handleEdit(match)} className="text-blue-600 hover:text-blue-900 mr-4"><SafeIcon icon={FiEdit} className="inline w-4 h-4" /></button><button onClick={() => handleDelete(match.id)} className="text-red-600 hover:text-red-900"><SafeIcon icon={FiTrash2} className="inline w-4 h-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredMatches.length === 0 && <p className="text-center py-8 text-gray-500">Δεν υπάρχουν αγώνες.</p>}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Matches;
